export const STOCK_TEMPLATE_KEYS = {
	CHECK_IN_PASS_MESSAGE: 'check_in_pass_message',
	EXPERIENCE_STORE_RECEIPT: 'experience_store_receipt',
	GROUP_LEADER_CHECKED_IN_CONFIRMATION_MESSAGE:
		'group_leader_checked_in_confirmation_message',
	GROUP_MEMBER_CHECKED_IN_CONFIRMATION_MESSAGE:
		'group_member_checked_in_confirmation_message',
	INDIVIDUAL_CHECKED_IN_CONFIRMATION_MESSAGE:
		'individual_checked_in_confirmation_message',
}

const LEGACY_TEMPLATE_KEY_ALIASES = {
	lead_checked_in_confirmation_message:
		STOCK_TEMPLATE_KEYS.GROUP_LEADER_CHECKED_IN_CONFIRMATION_MESSAGE,
}

export const STOCK_TEMPLATES = [
	{
		key: STOCK_TEMPLATE_KEYS.CHECK_IN_PASS_MESSAGE,
		name: 'Check-in Pass Message',
		description:
			'Sent on demand to travelers so they can receive and open their check-in pass.',
		type: 'on_demand',
		recipient: 'traveler',
		action: null,
		deliveryMethod: 'email',
		deliveryMethods: ['email', 'sms', 'both'],
		canonicalDescription: 'Check-in Pass Message',
		legacyDescriptions: ['Pass Email'],
		defaultSubject: 'Your Pass is Ready!',
		hasClickText: true,
		fields: [
			'emailSubject',
			'emailBody',
			'smsBody',
			'smsMediaUrl',
			'appendPassLinkToSms',
		],
		variables: [
			'{{travelerName}}',
			'{{clickHereToViewPass}}',
			'{{experienceName}}',
			'{{orgName}}',
		],
	},
	{
		key: STOCK_TEMPLATE_KEYS.EXPERIENCE_STORE_RECEIPT,
		name: 'Experience Store Receipt',
		description:
			'Automatically sent after a successful Experience Store purchase.',
		type: 'event_driven',
		recipient: 'traveler',
		action: 'on_experience_store_purchase',
		deliveryMethod: 'both',
		deliveryMethods: ['email', 'sms', 'both'],
		canonicalDescription: 'Experience Store Receipt',
		legacyDescriptions: [],
		defaultSubject: 'Your purchase receipt',
		hasClickText: false,
		hasReceiptSummary: true,
		fields: ['emailBody', 'smsBody', 'smsMediaUrl'],
		variables: [
			'{{travelerName}}',
			'{{experienceName}}',
			'{{orgName}}',
			'{{clickHereToViewPass}}',
			'{{orderNumber}}',
			'{{purchaseDate}}',
			'{{purchasedItems}}',
			'{{orderSubtotal}}',
			'{{orderFee}}',
			'{{orderTotal}}',
		],
	},
	{
		key: STOCK_TEMPLATE_KEYS.INDIVIDUAL_CHECKED_IN_CONFIRMATION_MESSAGE,
		name: 'Individual Checked-in Confirmation',
		description: 'Automatically sent when an individual check-in is completed.',
		type: 'event_driven',
		recipient: 'traveler',
		action: 'on_checkin',
		deliveryMethod: 'email',
		deliveryMethods: ['email', 'sms', 'both'],
		canonicalDescription: 'Individual Checked-in Confirmation',
		legacyDescriptions: [],
		defaultSubject: 'You are checked in',
		hasClickText: false,
		fields: ['emailBody', 'smsBody', 'smsMediaUrl'],
		variables: ['{{travelerName}}', '{{experienceName}}', '{{orgName}}'],
	},
	{
		key: STOCK_TEMPLATE_KEYS.GROUP_LEADER_CHECKED_IN_CONFIRMATION_MESSAGE,
		name: 'Group Leader Checked-in Confirmation',
		description:
			'Automatically sent to the group leader after the group check-in is completed.',
		type: 'event_driven',
		recipient: 'traveler',
		action: 'on_checkin',
		deliveryMethod: 'email',
		deliveryMethods: ['email', 'sms', 'both'],
		canonicalDescription: 'Group Leader Checked-in Confirmation',
		legacyDescriptions: ['Lead Checked-in Confirmation Message'],
		defaultSubject: 'Your group is checked in',
		hasClickText: false,
		fields: ['emailBody', 'smsBody', 'smsMediaUrl'],
		variables: [
			'{{travelerName}}',
			'{{groupMemberNames}}',
			'{{experienceName}}',
			'{{orgName}}',
		],
	},
	{
		key: STOCK_TEMPLATE_KEYS.GROUP_MEMBER_CHECKED_IN_CONFIRMATION_MESSAGE,
		name: 'Group Member Checked-in Confirmation',
		description:
			'Automatically sent to checked-in group members (excluding the leader) after the group check-in is completed.',
		type: 'event_driven',
		recipient: 'traveler',
		action: 'on_checkin',
		deliveryMethod: 'email',
		deliveryMethods: ['email', 'sms', 'both'],
		canonicalDescription: 'Group Member Checked-in Confirmation',
		legacyDescriptions: [],
		defaultSubject: 'Your group is checked in',
		hasClickText: false,
		fields: ['emailBody', 'smsBody', 'smsMediaUrl'],
		variables: [
			'{{travelerName}}',
			'{{groupLeaderName}}',
			'{{groupLeaderPhone}}',
			'{{experienceName}}',
			'{{orgName}}',
		],
	},
]

export const STOCK_TEMPLATE_MAP = Object.fromEntries(
	STOCK_TEMPLATES.map((template) => [template.key, template]),
)

export const getStockTemplate = (templateKey) =>
	STOCK_TEMPLATE_MAP[LEGACY_TEMPLATE_KEY_ALIASES[templateKey] || templateKey]
export const EMAIL_SEPARATOR_TOKEN = '[[separator]]'
export const DEFAULT_SEPARATOR_COLOR = '#8090B5'

export const isCheckInPassTemplate = (template) =>
	template?.key === STOCK_TEMPLATE_KEYS.CHECK_IN_PASS_MESSAGE

const VARIABLE_METADATA = {
	'{{travelerName}}': {
		key: 'travelerName',
		label: 'Traveler Name',
		description: "Recipient traveler's full name.",
		aliases: ['traveler', 'name'],
	},
	'{{groupLeaderName}}': {
		key: 'groupLeaderName',
		label: 'Group Leader Name',
		description: 'Primary group leader full name.',
		aliases: ['group leader', 'leader name'],
	},
	'{{groupLeaderPhone}}': {
		key: 'groupLeaderPhone',
		label: 'Group Leader Phone',
		description: "Primary group leader's phone number.",
		aliases: ['group leader phone', 'leader phone', 'phone'],
	},
	'{{groupMemberNames}}': {
		key: 'groupMemberNames',
		label: 'Group Member Names',
		description: 'Names of checked-in group members, excluding the leader.',
		aliases: ['group members', 'members', 'member names'],
	},
	'{{experienceName}}': {
		key: 'experienceName',
		label: 'Experience Name',
		description: 'Name of the current experience.',
		aliases: ['experience'],
	},
	'{{orgName}}': {
		key: 'orgName',
		label: 'Organization Name',
		description: 'Name of your organization.',
		aliases: ['organization', 'org'],
	},
	'{{clickHereToViewPass}}': {
		key: 'clickHereToViewPass',
		label: 'Pass Link URL',
		description: 'Direct link to the traveler pass.',
		aliases: ['pass link', 'pass url', 'link'],
	},
	'{{orderNumber}}': {
		key: 'orderNumber',
		label: 'Order Number',
		description: 'Short reference for the completed store order.',
		aliases: ['order', 'receipt number', 'reference'],
	},
	'{{purchaseDate}}': {
		key: 'purchaseDate',
		label: 'Purchase Date',
		description: 'Date the Experience Store purchase was completed.',
		aliases: ['date', 'order date'],
	},
	'{{purchasedItems}}': {
		key: 'purchasedItems',
		label: 'Purchased Items',
		description: 'Comma-separated names of the purchased items.',
		aliases: ['items', 'products'],
	},
	'{{orderSubtotal}}': {
		key: 'orderSubtotal',
		label: 'Order Subtotal',
		description: 'Purchase subtotal before any customer fee.',
		aliases: ['subtotal'],
	},
	'{{orderFee}}': {
		key: 'orderFee',
		label: 'Order Fee',
		description: 'Customer service fee charged on the order.',
		aliases: ['fee', 'service fee'],
	},
	'{{orderTotal}}': {
		key: 'orderTotal',
		label: 'Order Total',
		description: 'Total amount paid for the order.',
		aliases: ['total', 'amount paid'],
	},
}

const uniqueValues = (values = []) => Array.from(new Set(values.filter(Boolean)))

const tokenToKey = (token) =>
	String(token || '')
		.replace(/[{}]/g, '')
		.trim()

const humanizeKey = (key) => {
	const spaced = String(key || '')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.trim()
	if (!spaced) return 'Variable'
	return spaced
		.split(/\s+/)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ')
}

export const getVariableOption = (
	token,
	{ useExperiencePage = true } = {},
) => {
	const normalizedToken = String(token || '').trim()
	const baseMetadata = VARIABLE_METADATA[normalizedToken] || {}
	const metadata =
		normalizedToken === '{{clickHereToViewPass}}'
			? {
					...baseMetadata,
					label: useExperiencePage
						? 'Experience Page URL'
						: 'Direct Pass URL',
					description: useExperiencePage
						? "Link to the traveler's Experience Page, as configured in Experience Details > Advanced."
						: "Direct link to the traveler's check-in pass, as configured in Experience Details > Advanced.",
				}
			: baseMetadata
	const key = metadata.key || tokenToKey(normalizedToken)
	const aliases = uniqueValues([
		key,
		humanizeKey(key),
		...(Array.isArray(metadata.aliases) ? metadata.aliases : []),
	])

	return {
		token: normalizedToken,
		key,
		label: metadata.label || humanizeKey(key),
		description:
			metadata.description || 'Dynamic value inserted when the message is sent.',
		aliases,
	}
}

export const buildVariableOptions = (tokens = [], options = {}) =>
	uniqueValues(
		(Array.isArray(tokens) ? tokens : []).map((token) =>
			String(token || '').trim(),
		),
	)
		.filter(Boolean)
		.map((token) => getVariableOption(token, options))

export const buildPassPreviewUrl = (
	useExperiencePage = true,
	experienceId = '',
) => {
	const path = useExperiencePage === false ? 'get-pass' : 'myexperience'
	const normalizedExperienceId = String(experienceId || '').trim()
	return `https://livehq.co/${path}/preview${
		normalizedExperienceId
			? `/${encodeURIComponent(normalizedExperienceId)}`
			: ''
	}`
}

export const normalizeDeliveryMethod = (value) => {
	const normalized = String(value || 'email')
		.trim()
		.toLowerCase()
	if (normalized === 'text' || normalized === 'sms') return 'sms'
	if (normalized === 'both') return 'both'
	return 'email'
}

export const normalizeMessageAction = (value) => {
	if (value === null || value === undefined) return null
	const normalized = String(value)
		.trim()
		.toLowerCase()
	return normalized.length ? normalized : null
}

export const getSupportedDeliveryMethods = (template) => {
	const methods = Array.isArray(template?.deliveryMethods)
		? template.deliveryMethods
		: [template?.deliveryMethod || 'email']
	return Array.from(new Set(methods.map(normalizeDeliveryMethod)))
}

export const appendPassLinkToSmsBody = (body, clickUrl) => {
	const url = typeof clickUrl === 'string' ? clickUrl.trim() : ''
	const text = String(body || '').trimEnd()
	if (!url || text.includes(url)) return text
	return `${text}${text ? '\n' : ''}${url}`
}

export const normalizeTemplateData = (template, setupData = {}) => {
	const data =
		setupData && typeof setupData === 'object' && !Array.isArray(setupData)
			? setupData
			: {}

	return {
		emailSubject: data.emailSubject ?? data.subject ?? '',
		emailBody: data.emailBody ?? data.body ?? '',
		smsBody: data.smsBody ?? '',
		smsMediaUrl: data.smsMediaUrl ?? data.mmsMediaUrl ?? '',
		headerImageUrl: data.headerImageUrl ?? '',
		headerText: data.headerText ?? '',
		separatorColor: data.separatorColor ?? DEFAULT_SEPARATOR_COLOR,
		clickText:
			template?.hasClickText && typeof data.clickText === 'string'
				? data.clickText
				: 'CLICK HERE',
		appendPassLinkToSms: isCheckInPassTemplate(template)
			? data.appendPassLinkToSms !== false
			: false,
	}
}

export const getTemplateEditorDefaults = (template, setup) => {
	const normalized = normalizeTemplateData(template, setup?.data || {})
	return {
		deliveryMethod: normalizeDeliveryMethod(
			setup?.deliveryMethod || template?.deliveryMethod || 'email',
		),
		emailSubject: normalized.emailSubject || template?.defaultSubject || '',
		emailBody: normalized.emailBody || '',
		smsBody: normalized.smsBody || '',
		smsMediaUrl: normalized.smsMediaUrl || '',
		headerImageUrl: normalized.headerImageUrl || '',
		headerText: normalized.headerText || '',
		separatorColor: normalized.separatorColor || DEFAULT_SEPARATOR_COLOR,
		clickText: normalized.clickText || 'CLICK HERE',
		appendPassLinkToSms: normalized.appendPassLinkToSms,
	}
}

const getValidDescriptions = (template) => [
	template.canonicalDescription,
	...(template.legacyDescriptions || []),
]

export const doesSetupMatchTemplate = (setup, template) => {
	if (!setup || !template) return false
	return (
		setup.recipient === template.recipient &&
		normalizeMessageAction(setup.action) ===
			normalizeMessageAction(template.action) &&
		getValidDescriptions(template).includes(setup.description)
	)
}

export const findSetupForTemplate = (setups, template) => {
	if (!Array.isArray(setups) || !template) return null
	const descriptions = getValidDescriptions(template)

	const candidates = setups.filter(
		(setup) =>
			setup.recipient === template.recipient &&
			normalizeMessageAction(setup.action) ===
				normalizeMessageAction(template.action) &&
			descriptions.includes(setup.description),
	)
	if (!candidates.length) return null

	const preferredMethod = normalizeDeliveryMethod(template.deliveryMethod)
	const ranked = candidates
		.map((setup, index) => ({
			setup,
			index,
			descriptionScore: descriptions.indexOf(setup.description),
			methodScore:
				normalizeDeliveryMethod(setup.deliveryMethod) === preferredMethod
					? 0
					: 1,
		}))
		.sort((a, b) => {
			if (a.descriptionScore !== b.descriptionScore)
				return a.descriptionScore - b.descriptionScore
			if (a.methodScore !== b.methodScore) return a.methodScore - b.methodScore
			return a.index - b.index
		})

	return ranked[0].setup
}

const hasNonEmptyString = (value) =>
	typeof value === 'string' && value.trim().length > 0

export const isTemplateConfigured = (
	template,
	setupData,
	setupOrDeliveryMethod = 'email',
) => {
	if (!template || !setupData || typeof setupData !== 'object') return false

	const data = normalizeTemplateData(template, setupData)
	const deliveryMethod =
		typeof setupOrDeliveryMethod === 'object'
			? normalizeDeliveryMethod(setupOrDeliveryMethod.deliveryMethod)
			: normalizeDeliveryMethod(setupOrDeliveryMethod)

	const hasEmailContent =
		template.type === 'event_driven'
			? hasNonEmptyString(data.emailBody)
			: hasNonEmptyString(data.emailSubject) || hasNonEmptyString(data.emailBody)
	const hasSmsContent = hasNonEmptyString(data.smsBody)

	if (deliveryMethod === 'sms') return hasSmsContent
	if (deliveryMethod === 'both') {
		return hasEmailContent && hasSmsContent
	}
	return hasEmailContent
}

const replaceToken = (value, token, replacement) =>
	String(value || '')
		.split(token)
		.join(replacement)

const escapeHtml = (value) =>
	String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')

const sanitizeSeparatorColor = (value) =>
	/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || '').trim())
		? String(value).trim()
		: DEFAULT_SEPARATOR_COLOR

const normalizeReceiptContext = (receipt = {}) => {
	const safe = receipt && typeof receipt === 'object' && !Array.isArray(receipt)
		? receipt
		: {}
	const items = Array.isArray(safe.items)
		? safe.items
				.map((item) => ({
					name: String(item?.name || '').trim(),
					quantity: Math.max(1, Number.parseInt(item?.quantity, 10) || 1),
					amount: String(item?.amount || '').trim(),
				}))
				.filter((item) => item.name)
		: []
	return {
		orderNumber: String(safe.orderNumber || '').trim(),
		purchaseDate: String(safe.purchaseDate || '').trim(),
		items,
		subtotal: String(safe.subtotal || '').trim(),
		fee: String(safe.fee || '').trim(),
		total: String(safe.total || '').trim(),
		showFee: safe.showFee === true,
	}
}

export const buildReceiptSummaryHtml = (receiptInput = {}) => {
	const receipt = normalizeReceiptContext(receiptInput)
	if (!receipt.items.length) return ''
	const meta = [
		receipt.orderNumber ? `Order ${escapeHtml(receipt.orderNumber)}` : '',
		receipt.purchaseDate ? escapeHtml(receipt.purchaseDate) : '',
	]
		.filter(Boolean)
		.join(' &nbsp;&middot;&nbsp; ')
	const itemRows = receipt.items
		.map(
			(item) =>
				`<tr><td style="padding:12px 0;border-bottom:1px solid #e7e8ec;font-size:15px;line-height:1.4;color:#17181a;">${
					item.quantity > 1
						? `<span style="color:#686b73;">${item.quantity}&times;</span> `
						: ''
				}${escapeHtml(item.name)}</td><td style="padding:12px 0 12px 16px;border-bottom:1px solid #e7e8ec;text-align:right;white-space:nowrap;font-size:15px;color:#17181a;">${escapeHtml(
					item.amount,
				)}</td></tr>`,
		)
		.join('')
	const feeRow = receipt.showFee
		? `<tr><td style="padding:5px 0;color:#686b73;font-size:14px;">Service fee</td><td style="padding:5px 0;text-align:right;color:#686b73;font-size:14px;">${escapeHtml(
				receipt.fee,
			)}</td></tr>`
		: ''
	return `<div style="margin:24px 0 0;padding:20px;border:1px solid #e1e3e8;border-radius:10px;background:#fafafa;"><div style="font-size:18px;font-weight:700;color:#17181a;">Order summary</div>${
		meta
			? `<div style="margin-top:5px;font-size:13px;color:#777a82;">${meta}</div>`
			: ''
	}<table role="presentation" style="width:100%;border-collapse:collapse;margin-top:12px;">${itemRows}<tr><td style="padding:12px 0 5px;color:#686b73;font-size:14px;">Subtotal</td><td style="padding:12px 0 5px;text-align:right;color:#686b73;font-size:14px;">${escapeHtml(
		receipt.subtotal,
	)}</td></tr>${feeRow}<tr><td style="padding:10px 0 0;font-size:16px;font-weight:700;color:#17181a;">Total</td><td style="padding:10px 0 0;text-align:right;font-size:16px;font-weight:700;color:#17181a;">${escapeHtml(
		receipt.total,
	)}</td></tr></table></div>`
}

export const renderEmailBodyHtml = ({
	template,
	emailBody,
	clickText,
	clickUrl,
	travelerName,
	groupLeaderName,
	groupLeaderPhone,
	groupMemberNames,
	experienceName,
	orgName,
	orderNumber,
	purchaseDate,
	purchasedItems,
	orderSubtotal,
	orderFee,
	orderTotal,
	separatorColor,
}) => {
	const safeSeparatorColor = sanitizeSeparatorColor(separatorColor)
	const clickMarkup = `<a href="${clickUrl}" target="_blank" rel="noopener noreferrer" style="color:#0a7cff; font-weight:700; text-decoration:none;">${
		clickText || 'CLICK HERE'
	}</a>`
	const clickPlaceholder = '__CLICK_LINK_PLACEHOLDER__'

	let output = emailBody || ''
	output = replaceToken(output, '{{travelerName}}', travelerName || '')
	output = replaceToken(output, '{{groupLeaderName}}', groupLeaderName || '')
	output = replaceToken(output, '{{groupLeaderPhone}}', groupLeaderPhone || '')
	output = replaceToken(output, '{{groupMemberNames}}', groupMemberNames || '')
	output = replaceToken(output, '{{experienceName}}', experienceName || '')
	output = replaceToken(output, '{{orgName}}', orgName || '')
	output = replaceToken(output, '{{orderNumber}}', orderNumber || '')
	output = replaceToken(output, '{{purchaseDate}}', purchaseDate || '')
	output = replaceToken(output, '{{purchasedItems}}', purchasedItems || '')
	output = replaceToken(output, '{{orderSubtotal}}', orderSubtotal || '')
	output = replaceToken(output, '{{orderFee}}', orderFee || '')
	output = replaceToken(output, '{{orderTotal}}', orderTotal || '')
	output = replaceToken(
		output,
		'{{clickHereToViewPass}}',
		template?.hasClickText ? clickPlaceholder : clickUrl || '',
	)

	return output
		.split('\n')
		.map((line) => {
			if (line.trim() === EMAIL_SEPARATOR_TOKEN) {
				return `<div style="height:8px;background:${safeSeparatorColor};margin:20px 0;"></div>`
			}
			if (!line.trim()) {
				return '<div style="height:14px;"></div>'
			}
			const escaped = escapeHtml(line)
			const withLink = escaped.split(clickPlaceholder).join(clickMarkup)
			return `<div style="margin:0 0 14px;font-size:18px;line-height:1.55;color:#111111;">${withLink}</div>`
		})
		.join('')
}

export const buildStandardEmailHtml = ({
	headerImageUrl,
	headerText,
	bodyHtml,
}) => {
	const imageHtml = hasNonEmptyString(headerImageUrl)
		? `<img src="${headerImageUrl}" alt="Email header" style="display:block;width:100%;height:auto;max-height:220px;object-fit:cover;" />`
		: ''
	const titleHtml = hasNonEmptyString(headerText)
		? `<h1 style="margin:0 0 18px;font-size:30px;line-height:1.05;font-weight:800;color:#0f0f0f;font-family:Arial,Helvetica,sans-serif;">${escapeHtml(
				headerText,
			)}</h1>`
		: ''

	return `<div style="padding:24px 12px;"><div style="width:100%;max-width:640px;margin:0 auto;">${imageHtml}<div style="padding:18px 16px 26px;font-family:Arial,Helvetica,sans-serif;">${titleHtml}${bodyHtml}</div></div></div>`
}
